import { useEffect, useState } from 'react'
import { Modal, ModalBody, Spinner } from 'reactstrap'
// @ts-ignore
import { Button, Input, Textarea } from 'dsm-react-lib'
// @ts-ignore
import Select from 'react-select'
import { IconStack2, IconX, IconWand, IconRefresh, IconBulb, IconCheck } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { selectThemeColors } from '../../../../utility/Utils'
import { useAgentService } from '../agent/agentService'
import {
  PROJECT_MAX_LENGTH,
  INSTRUCTIONS_MAX_LENGTH,
  PROJECT_ICONS,
  PROJECT_COLOURS,
  PROJECT_MEMORY_MODES,
  resolveMemoryMode,
  getProjectIcon,
  getProjectColour
} from './projectConstants'

/** Existing project values used to seed the form in edit mode. */
export interface ProjectFormValue {
  id?: string
  name: string
  icon: string
  color: string
  instructions?: string
  /** List/get responses return the string name (e.g. "Default"); request bodies use the numeric enum. */
  memoryMode?: number | string
}

/** Normalised payload emitted on submit for both create and edit. */
export interface ProjectFormPayload {
  name: string
  icon: string
  color: string
  instructions: string
  memoryMode: number
}

interface ProjectFormModalProps {
  isOpen: boolean
  toggle: () => void
  /** Pass an existing project to render in edit mode; omit/null for create mode. */
  project?: ProjectFormValue | null
  onSubmit: (payload: ProjectFormPayload) => void
  isSubmitting?: boolean
}

const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  isOpen,
  toggle,
  project = null,
  onSubmit,
  isSubmitting = false
}) => {
  const isEditMode = !!project?.id

  const [projectName, setProjectName] = useState('')
  const [selectedIcon, setSelectedIcon] = useState(PROJECT_ICONS[0].id)
  const [selectedColour, setSelectedColour] = useState(PROJECT_COLOURS[0].id)
  const [instructions, setInstructions] = useState('')
  const [memoryMode, setMemoryMode] = useState<number>(PROJECT_MEMORY_MODES[0].value)
  const [showIconPanel, setShowIconPanel] = useState(false)

  // Generate-with-AI (system instructions) state
  const { generateSystemPrompt } = useAgentService()
  const [showPromptBox, setShowPromptBox] = useState(false)
  const [promptText, setPromptText] = useState('')
  const [isPromptDescValid, setIsPromptDescValid] = useState(true)
  const [originalPromptText, setOriginalPromptText] = useState('')
  const [aiImprovedPrompt, setAiImprovedPrompt] = useState('')
  const [isLoadingPromptResponse, setIsLoadingPromptResponse] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [textareaKey, setTextareaKey] = useState(0)

  // Seed/reset the form whenever the modal opens or the project to edit changes
  useEffect(() => {
    if (!isOpen) return
    setProjectName(project?.name ?? '')
    setSelectedIcon(project?.icon || PROJECT_ICONS[0].id)
    setSelectedColour(project?.color || PROJECT_COLOURS[0].id)
    setInstructions(project?.instructions ?? '')
    setMemoryMode(resolveMemoryMode(project?.memoryMode))
    // Create mode opens the icon/colour picker by default; edit mode keeps it collapsed
    setShowIconPanel(!project?.id)
    setShowPromptBox(false)
    setPromptText('')
    setIsPromptDescValid(true)
    setOriginalPromptText('')
    setAiImprovedPrompt('')
    setIsRegenerating(false)
    setTextareaKey((prev) => prev + 1) // Force remount to show correct counter for existing instructions
  }, [isOpen, project])

  const selectedColourValue = getProjectColour(selectedColour)
  const SelectedIconComponent = getProjectIcon(selectedIcon)

  const handleSubmit = () => {
    if (!projectName.trim()) return
    onSubmit({
      name: projectName.trim(),
      icon: selectedIcon,
      color: selectedColour,
      instructions: instructions.trim(),
      memoryMode
    })
  }

  // Generate-with-AI handlers
  const generateSystemInstructions = async (prompt: string) => {
    if (prompt.trim() === '') {
      setIsPromptDescValid(false)
      return
    }
    return await generateSystemPrompt(prompt, 'systemPrompt')
  }

  const handleGeneratePrompt = async () => {
    if (promptText.trim()) {
      setOriginalPromptText(promptText)
      setIsLoadingPromptResponse(true)
      try {
        const result: any = await generateSystemInstructions(promptText)
        const improvedText = result?.content || ''
        setAiImprovedPrompt(improvedText)
        setInstructions(improvedText)
        setTextareaKey((prev) => prev + 1) // Force remount to update counter
      } catch (error) {
        toast.error('Error generating instructions')
      } finally {
        setIsLoadingPromptResponse(false)
      }
    }
  }

  const handleAcceptPrompt = () => {
    setPromptText('')
    setOriginalPromptText('')
    setAiImprovedPrompt('')
    setShowPromptBox(false)
  }

  const handleDiscardPrompt = () => {
    setInstructions('')
    setPromptText('')
    setOriginalPromptText('')
    setAiImprovedPrompt('')
    setShowPromptBox(false)
  }

  const handleRegeneratePrompt = async () => {
    if (originalPromptText.trim()) {
      setIsLoadingPromptResponse(true)
      setIsRegenerating(true)
      try {
        const result: any = await generateSystemInstructions(originalPromptText)
        const newImprovedText = result?.content || ''
        setAiImprovedPrompt(newImprovedText)
        setInstructions(newImprovedText)
        setTextareaKey((prev) => prev + 1) // Force remount to update counter
      } catch (error) {
        toast.error('Error regenerating instructions')
      } finally {
        setIsLoadingPromptResponse(false)
        setIsRegenerating(false)
      }
    }
  }

  const submitLabel = isEditMode
    ? isSubmitting
      ? 'Saving...'
      : 'Save changes'
    : isSubmitting
    ? 'Creating...'
    : 'Create project'

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="!max-w-[600px] !w-[600px]"
      contentClassName="overflow-hidden !rounded-lg border-0 shadow-lg"
      data-testid="project-form-modal">
      {/* HEADER */}
      <div
        className={`flex items-center justify-between chat-primary theme-bg bg-[var(--color-primary)] modal-header py-1 pl-6 pr-4`}>
        <div className="flex items-center gap-[8px]">
          <IconStack2 size={24} color="white" />
          <span className="text-white text-base font-semibold leading-none">
            {isEditMode ? 'Project settings' : 'Create project'}
          </span>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="text-white opacity-80 hover:opacity-100 transition-opacity cursor-pointer bg-transparent border-0 p-0 flex items-center"
          aria-label="Close"
          data-testid="close-button">
          <IconX size={20} />
        </button>
      </div>

      {/* CONTAINER */}
      <ModalBody className="theme-surface !p-4 flex flex-col gap-2">
        {/* name + icon */}
        <div className="flex flex-col gap-1">
          {/* row 1: icon preview + name field */}
          <div className="flex gap-2">
            {/* icon preview box */}
            <button
              type="button"
              onClick={() => setShowIconPanel((prev) => !prev)}
              aria-label="Set project icon and colour"
              aria-expanded={showIconPanel}
              className="flex-shrink-0 flex items-center justify-center rounded-lg cursor-pointer border-0 transition-all hover:opacity-90 w-24 h-24 theme-surface-muted shadow-sm">
              <SelectedIconComponent
                size={48}
                className="text-[color:var(--selected-icon-colour)]"
                style={{ ['--selected-icon-colour' as any]: selectedColourValue }}
                stroke={1.5}
              />
            </button>

            {/* name field column */}
            <div className="flex-1 flex flex-col gap-1.5">
              <label htmlFor="project-name" className="theme-text-heading text-sm font-semibold">
                Project name
              </label>
              <Input
                type="text"
                id="project-name"
                name="project-name"
                placeholder="e.g. Global Equities"
                value={projectName}
                maxLength={PROJECT_MAX_LENGTH}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setProjectName(e.target.value)
                }
                data-testid="project-name-input"
              />
            </div>
          </div>

          {/* icon & colour options panel */}
          {showIconPanel && (
            <div className="relative rounded-lg theme-surface-muted">
              {/* pointer arrow */}
              <div className="absolute -top-[7px] left-[42px] w-0 h-0 border-x-8 border-x-transparent border-b-8 border-b-[var(--surface-muted)]" />

              {/* content */}
              <div className="flex flex-col gap-2 p-2">
                {/* heading row */}
                <div className="flex items-center justify-between">
                  <span className="text-sm theme-text-heading">
                    Set an icon and colour to help identify this project
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowIconPanel(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-0 p-0 flex items-center cursor-pointer ml-2 flex-shrink-0"
                    aria-label="Dismiss icon panel">
                    <IconX size={16} />
                  </button>
                </div>

                {/* colour group: icons + divider + colours */}
                <div className="flex flex-col gap-2">
                  {/* icon row */}
                  <div className="flex items-center gap-1">
                    {PROJECT_ICONS.map(({ id, Icon, label }) => (
                      <button
                        key={id}
                        type="button"
                        title={label}
                        aria-label={`Select icon: ${label}`}
                        aria-pressed={selectedIcon === id}
                        onClick={() => setSelectedIcon(id)}
                        className={`py-[5px] flex items-center justify-center rounded-md transition-all cursor-pointer border-0 ${
                          selectedIcon === id
                            ? 'bg-[var(--color-primary)]'
                            : 'bg-transparent theme-hover-surface'
                        }`}>
                        <Icon
                          size={20}
                          stroke={1.5}
                          className={selectedIcon === id ? 'text-white' : 'theme-text-heading'}
                        />
                      </button>
                    ))}
                  </div>

                  <div className="h-px w-full bg-gray-300" />

                  {/* colour row */}
                  <div className="flex items-center gap-1">
                    {PROJECT_COLOURS.map(({ id, value }) => (
                      <button
                        key={id}
                        type="button"
                        title={id}
                        aria-label={`Select colour: ${id}`}
                        aria-pressed={selectedColour === id}
                        onClick={() => setSelectedColour(id)}
                        className="!w-7 !h-7 !rounded-full flex items-center justify-center transition-all cursor-pointer border-0 flex-shrink-0 bg-[color:var(--swatch-colour)]"
                        style={{ ['--swatch-colour' as any]: value }}>
                        {selectedColour === id && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            aria-hidden="true">
                            <path
                              d="M2 7L5.5 10.5L12 3.5"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* instructions */}
        <div className="flex flex-col gap-1">
          <div className="flex justify-between items-center">
            <label
              htmlFor="project-instructions"
              className="theme-text-heading text-sm font-semibold">
              Instructions
            </label>
            <div
              className={`flex items-center gap-50 p-50 rounded transition-colors select-none ${
                isLoadingPromptResponse
                  ? 'theme-text-subheading theme-surface-muted cursor-not-allowed'
                  : 'text-[var(--brand-accent-color)] hover:bg-[var(--brand-accent-bg)] cursor-pointer'
              }`}
              onClick={!isLoadingPromptResponse ? () => setShowPromptBox((v) => !v) : undefined}
              data-testid="generate-instructions-button">
              <IconWand
                size={18}
                className={
                  isLoadingPromptResponse
                    ? 'theme-text-subheading'
                    : 'text-[var(--brand-accent-color)]'
                }
              />
              <span>{isLoadingPromptResponse ? 'Generating...' : 'Generate with AI'}</span>
            </div>
          </div>
          <Textarea
            key={`textarea-${textareaKey}`}
            id="project-instructions"
            name="project-instructions"
            rows={3}
            maxLength={INSTRUCTIONS_MAX_LENGTH}
            counterType="basic"
            value={instructions}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setInstructions(e.target.value)
            }
            placeholder="Add project context and rules e.g. Use clear, structured responses. Ask clarifying questions when requirements are unclear, and avoid making assumptions."
            data-testid="project-instructions-input"
          />

          {showPromptBox && (
            <div className="mt-1 theme-surface-muted rounded-lg border theme-border-default p-1">
              <Textarea
                className="w-full theme-surface border border-[var(--border-default)] rounded-md p-1 resize-none"
                value={promptText}
                onChange={(e: any) => {
                  setPromptText(e.target.value)
                  if (e.target.value.trim() !== '') {
                    setIsPromptDescValid(true)
                  }
                }}
                placeholder="Describe what this project should help with or how the assistant should behave e.g.: 'A research assistant that compares ESG metrics across portfolios and writes clear summaries.'"
                rows={3}
                invalid={!isPromptDescValid}
                helperText={isPromptDescValid ? '' : 'Prompt description cannot be empty'}
              />

              <div className="flex justify-between items-center mt-2">
                <Button
                  className="flex items-center gap-50 p-1 text-white rounded transition-colors"
                  onClick={handleGeneratePrompt}
                  color="gray"
                  icon={
                    isLoadingPromptResponse ? (
                      <Spinner size="sm" style={{ width: 16, height: 16 }} />
                    ) : (
                      <IconWand color="white" size={16} />
                    )
                  }
                  label={isLoadingPromptResponse ? 'Generating...' : 'Generate prompt'}
                  disabled={promptText === '' || isLoadingPromptResponse}
                />

                <div className="flex justify-end mt-0">
                  <div
                    className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                      isLoadingPromptResponse || !aiImprovedPrompt
                        ? 'theme-text-subheading cursor-not-allowed'
                        : 'theme-text-body cursor-pointer theme-hover-surface'
                    }`}
                    onClick={
                      !isLoadingPromptResponse && aiImprovedPrompt ? handleDiscardPrompt : undefined
                    }
                    data-testid="discard-prompt-button">
                    <IconX size={18} />
                    <span>Discard</span>
                  </div>
                  <div
                    onClick={
                      !isLoadingPromptResponse && aiImprovedPrompt
                        ? handleRegeneratePrompt
                        : undefined
                    }
                    className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                      isLoadingPromptResponse || !aiImprovedPrompt
                        ? 'theme-text-subheading cursor-not-allowed'
                        : 'theme-text-body cursor-pointer theme-hover-surface'
                    }`}
                    data-testid="regenerate-prompt-button">
                    <IconRefresh size={16} />
                    <span>{isRegenerating ? 'Regenerating...' : 'Regenerate'}</span>
                  </div>
                  <div
                    onClick={
                      !isLoadingPromptResponse && aiImprovedPrompt ? handleAcceptPrompt : undefined
                    }
                    className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                      isLoadingPromptResponse || !aiImprovedPrompt
                        ? 'theme-text-subheading cursor-not-allowed'
                        : 'theme-text-body cursor-pointer theme-hover-surface'
                    }`}
                    data-testid="accept-prompt-button">
                    <IconCheck size={18} />
                    <span>Accept</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* memory */}
        <div className="flex flex-col gap-1">
          <label htmlFor="project-memory" className="theme-text-heading text-sm font-semibold">
            Memory
          </label>
          <Select
            inputId="project-memory"
            name="project-memory"
            options={PROJECT_MEMORY_MODES}
            value={PROJECT_MEMORY_MODES.find((option) => option.value === memoryMode) ?? null}
            onChange={(selectedOption: any) =>
              setMemoryMode(selectedOption?.value ?? PROJECT_MEMORY_MODES[0].value)
            }
            isClearable={false}
            classNamePrefix="select"
            className="react-select"
            placeholder="Select memory"
            theme={selectThemeColors}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            data-testid="project-memory-select"
          />
          <span className="text-xs theme-text-subheading">
            Controls how context from this project is retained and reused.
          </span>
        </div>

        {/* info banner - only show in create mode */}
        {!isEditMode && (
          <div className="flex items-start gap-1 p-1 rounded-lg theme-surface-muted">
            <IconBulb size={24} className="flex-shrink-0 mt-0.5 theme-text-heading" stroke={1.5} />
            <span className="theme-text-body leading-relaxed">
              Projects keep chats, files, and custom instructions in one place. Use them for ongoing
              work, or just to keep things tidy.
            </span>
          </div>
        )}

        {/* actions */}
        <div className="flex items-center justify-end gap-2">
          <Button
            label="Cancel"
            variant="outline"
            color="neutral"
            className="dsm-btn-neutral"
            onClick={toggle}
            data-testid="cancel-button"
          />
          <Button
            label={submitLabel}
            color="primary"
            disabled={!projectName.trim() || isSubmitting}
            onClick={handleSubmit}
            data-testid="submit-project-button"
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ProjectFormModal
